import * as THREE from 'three';

export type StyleName = 'dark' | 'light';

export interface StyleDefinition {
  label: string;
  background: string;
  fog: { color: string; highColor: string; blend: number; spaceColor: string; stars: number };
  lighting: {
    ambient: { color: number; intensity: number };
    key: { color: number; intensity: number };
    fill: { color: number; intensity: number };
    hemi: { sky: number; ground: number; intensity: number };
  };
  grid: { majorColor: number; minorColor: number; opacity: number };
  categories: Record<string, { emissive: number; edgeBrightness: number }>;
  material(origColor: number, category: string): {
    color: THREE.Color;
    emissive: THREE.Color;
    emissiveIntensity: number;
    shininess: number;
    opacity: number;
    edgeColor: THREE.Color;
  };
}

export const TRON_CATEGORIES: Record<string, { emissive: number; edgeBrightness: number }> = {
  infrastructure: { emissive: 0x1d4b6c, edgeBrightness: 0.7 },
  utility: { emissive: 0xff4422, edgeBrightness: 1.0 },
  residential: { emissive: 0x265f63, edgeBrightness: 0.6 },
  commercial: { emissive: 0x423f67, edgeBrightness: 0.65 },
  community: { emissive: 0x30303a, edgeBrightness: 0.55 },
  vehicle: { emissive: 0x06b6d4, edgeBrightness: 1.0 },
  roads: { emissive: 0x275770, edgeBrightness: 0.7 },
  nature: { emissive: 0x154a70, edgeBrightness: 0.5 },
};

const SKETCH_CATEGORIES: Record<string, { emissive: number; edgeBrightness: number }> = {
  infrastructure: { emissive: 0x000000, edgeBrightness: 1.0 },
  utility: { emissive: 0x000000, edgeBrightness: 1.0 },
  residential: { emissive: 0x000000, edgeBrightness: 1.0 },
  commercial: { emissive: 0x000000, edgeBrightness: 1.0 },
  community: { emissive: 0x000000, edgeBrightness: 1.0 },
  vehicle: { emissive: 0x000000, edgeBrightness: 1.0 },
  roads: { emissive: 0x000000, edgeBrightness: 1.0 },
  nature: { emissive: 0x000000, edgeBrightness: 1.0 },
};

export const STYLES: Record<StyleName, StyleDefinition> = {
  dark: {
    label: 'Dark',
    background: '#121a24',
    fog: { color: '#121a24', highColor: '#151e28', blend: 0.08, spaceColor: '#0e1620', stars: 0.1 },
    lighting: {
      ambient: { color: 0x111122, intensity: 0.5 },
      key: { color: 0x223355, intensity: 0.8 },
      fill: { color: 0x112244, intensity: 0.4 },
      hemi: { sky: 0x111133, ground: 0x000000, intensity: 0.3 },
    },
    grid: { majorColor: 0x44bbee, minorColor: 0x3399cc, opacity: 0.12 },
    categories: TRON_CATEGORIES,
    material(origColor, category) {
      const cat = TRON_CATEGORIES[category] ?? TRON_CATEGORIES.commercial;
      const isRoad = category === 'roads';
      return {
        color: new THREE.Color(origColor).multiplyScalar(isRoad ? 0.08 : 0.2),
        emissive: new THREE.Color(cat.emissive),
        emissiveIntensity: isRoad ? 0.3 : 0.22,
        shininess: 12,
        opacity: 0.92,
        edgeColor: new THREE.Color(cat.emissive).multiplyScalar(1.8),
      };
    },
  },

  light: {
    label: 'Light',
    background: '#f0f0f0',
    fog: { color: '#f0f0f0', highColor: '#f0f0f0', blend: 1.0, spaceColor: '#f0f0f0', stars: 0 },
    lighting: {
      ambient: { color: 0xffffff, intensity: 1.0 },
      key: { color: 0xffffff, intensity: 1.5 },
      fill: { color: 0xffffff, intensity: 0.8 },
      hemi: { sky: 0xffffff, ground: 0xcccccc, intensity: 0.6 },
    },
    grid: { majorColor: 0xdddddd, minorColor: 0xeeeeee, opacity: 0.05 },
    categories: SKETCH_CATEGORIES,
    material(_origColor, category) {
      const isRoad = category === 'roads';
      const color = isRoad ? 0xdddddd : 0xffffff;
      return {
        color: new THREE.Color(color),
        emissive: new THREE.Color(0x000000),
        emissiveIntensity: 0,
        shininess: 5,
        opacity: 1.0,
        edgeColor: new THREE.Color(0x666666),
      };
    },
  },
};
